import { useState } from 'react';
import { Linkedin, Facebook, Twitter, Link2, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Social Media Share Component
 * Supports sharing to LinkedIn, Facebook, Twitter/X, and Copy Link
 * 
 * @param {string} type - Type of share: 'certificate', 'badge', 'level'
 * @param {string} title - Title of the achievement
 * @param {string} description - Description text
 * @param {string} shareUrl - The URL to share
 * @param {string} variant - 'buttons' (default) or 'compact' or 'inline'
 */
const SocialShare = ({ 
  type = 'achievement',
  title,
  description,
  shareUrl,
  variant = 'buttons'
}) => {
  const [copied, setCopied] = useState(false);
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const fullUrl = shareUrl?.startsWith('http') ? shareUrl : `${baseUrl}${shareUrl}`;
  
  // Craft share messages based on type
  const getShareText = () => {
    switch (type) {
      case 'certificate':
        return `I just earned a certificate in "${title}" from NextGen Tech Pathways! #TechEducation #Coding #NextGenTechPathways`;
      case 'badge':
        return `I just earned the "${title}" badge on NextGen Tech Pathways! ${description} #Achievement #Coding #NextGenTechPathways`;
      case 'level':
        return `I just reached ${title} level on NextGen Tech Pathways! ${description} #LevelUp #Coding #NextGenTechPathways`;
      default:
        return `Check out my achievement on NextGen Tech Pathways! ${title} #NextGenTechPathways`;
    }
  };

  const shareText = getShareText();
  
  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}&quote=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(fullUrl)}`
  };

  const handleShare = (platform) => {
    const url = shareLinks[platform];
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Compact variant - just icons in a row
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleShare('linkedin')}
          data-testid="share-linkedin"
          className="w-8 h-8 rounded-full bg-[#0077B5]/20 flex items-center justify-center text-[#0077B5] hover:bg-[#0077B5] hover:text-white transition-all"
          title="Share on LinkedIn"
        >
          <Linkedin className="w-4 h-4" />
        </button>
        <button
          onClick={() => handleShare('facebook')}
          data-testid="share-facebook"
          className="w-8 h-8 rounded-full bg-[#1877F2]/20 flex items-center justify-center text-[#1877F2] hover:bg-[#1877F2] hover:text-white transition-all"
          title="Share on Facebook"
        >
          <Facebook className="w-4 h-4" />
        </button>
        <button
          onClick={() => handleShare('twitter')}
          data-testid="share-twitter"
          className="w-8 h-8 rounded-full bg-[#1DA1F2]/20 flex items-center justify-center text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white transition-all"
          title="Share on X (Twitter)"
        >
          <Twitter className="w-4 h-4" />
        </button>
        <button
          onClick={handleCopyLink}
          data-testid="share-copy-link"
          className="w-8 h-8 rounded-full bg-[#94A3B8]/20 flex items-center justify-center text-[#94A3B8] hover:bg-[#00FFE0] hover:text-[#060F22] transition-all"
          title="Copy Link"
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  // Inline variant - single share button with dropdown
  if (variant === 'inline') {
    return (
      <div className="relative group">
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#00AEEF]/20 text-[#00AEEF] hover:bg-[#00AEEF]/30 transition-all text-sm font-medium"
          data-testid="share-button"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
        <div className="absolute top-full right-0 mt-2 bg-[#0A1F44] border border-[#00AEEF]/30 rounded-xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl z-50 min-w-[160px]">
          <button
            onClick={() => handleShare('linkedin')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#94A3B8] hover:bg-[#0077B5]/20 hover:text-[#0077B5] transition-all text-sm"
          >
            <Linkedin className="w-4 h-4" />
            LinkedIn
          </button>
          <button
            onClick={() => handleShare('facebook')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#94A3B8] hover:bg-[#1877F2]/20 hover:text-[#1877F2] transition-all text-sm"
          >
            <Facebook className="w-4 h-4" />
            Facebook
          </button>
          <button
            onClick={() => handleShare('twitter')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#94A3B8] hover:bg-[#1DA1F2]/20 hover:text-[#1DA1F2] transition-all text-sm"
          >
            <Twitter className="w-4 h-4" />
            X (Twitter)
          </button>
          <div className="border-t border-[#00AEEF]/20 my-1"></div>
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#94A3B8] hover:bg-[#00FFE0]/20 hover:text-[#00FFE0] transition-all text-sm"
          >
            {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>
    );
  }

  // Default buttons variant - full buttons with labels
  return (
    <div className="space-y-3">
      <p className="text-sm text-[#94A3B8] font-medium">Share your achievement:</p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleShare('linkedin')}
          data-testid="share-linkedin"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0077B5] text-white hover:bg-[#005885] transition-all font-medium text-sm"
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </button>
        <button
          onClick={() => handleShare('facebook')}
          data-testid="share-facebook"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1877F2] text-white hover:bg-[#0d65d9] transition-all font-medium text-sm"
        >
          <Facebook className="w-4 h-4" />
          Facebook
        </button>
        <button
          onClick={() => handleShare('twitter')}
          data-testid="share-twitter"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1DA1F2] text-white hover:bg-[#0c8de0] transition-all font-medium text-sm"
        >
          <Twitter className="w-4 h-4" />
          X (Twitter)
        </button>
        <button
          onClick={handleCopyLink}
          data-testid="share-copy-link"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all font-medium text-sm ${
            copied 
              ? 'bg-[#00FFE0] text-[#060F22]' 
              : 'bg-[#0A1F44] text-[#94A3B8] border border-[#00AEEF]/30 hover:border-[#00FFE0] hover:text-[#00FFE0]'
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
};

export default SocialShare;
